/**
 * What the office has decided about one driver.
 *
 * **Built around a settings document, not around the one field it has today.** That is the
 * actual point of this component: the next per-driver setting is a property on
 * `DriverSettings` and another row in this form, never a second dialog and a second fetch.
 * A screen shaped around its only field is a screen that gets duplicated the moment there
 * are two.
 *
 * On the fleet page rather than a page of its own, because this is already where an
 * administrator picks a driver, and it already refetches after every mutation since these
 * records interact. A second place to open one person would be two half-screens about them.
 *
 * *Follow the firm* is the first option and maps to null. It is a real answer rather than
 * the absence of one, and the sentence beside it says what the firm's answer currently is —
 * otherwise choosing it means holding two settings in your head at once.
 *
 * It saves on the choice rather than on a button, as it always has, so it carries no save
 * bar and takes no part in the page's.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ThemePreference } from '../api';
import { describe } from '../errors';
import { Banner, Button, Dialog, EmptyState, Field, Select } from '../../ui';
import { fleetApi, type DriverSettings } from './api';

const OPTIONS: readonly ThemePreference[] = ['System', 'Light', 'Dark'];

export function DriverSettingsForm({
  driverId,
  driverName,
  onClose,
}: {
  driverId: string;
  driverName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const [settings, setSettings] = useState<DriverSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(async () => {
    setProblem(null);

    try {
      setSettings(await fleetApi.driverSettings(driverId));
    } catch (error) {
      setProblem(describe(error, t, 'settings.couldNotLoad'));
    }
  }, [driverId, t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();
  }, [load]);

  async function save(appTheme: ThemePreference | null) {
    setSaving(true);
    setProblem(null);

    try {
      setSettings(await fleetApi.saveDriverSettings(driverId, { appTheme }));
    } catch (error) {
      setProblem(describe(error, t, 'settings.couldNotSave'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open
      title={`${t('fleet.drivers.settings')} · ${driverName}`}
      onClose={onClose}
      actions={
        <Button variant="quiet" onClick={onClose}>
          {t('fleet.close')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {problem !== null && <Banner tone="danger">{problem}</Banner>}

        {settings === null ? (
          // The failure replaces the wait rather than sitting above it: a form that says
          // "loading" for ever is how a refusal gets read as a slow network.
          problem === null && <EmptyState>{t('settings.loading')}</EmptyState>
        ) : (
          <Field
            label={t('fleet.drivers.appTheme')}
            help={t('fleet.drivers.appThemeHelp', {
              firm: t(`theme.names.${settings.effectiveAppTheme.toLowerCase()}`),
            })}
          >
            <Select
              value={settings.appTheme ?? ''}
              disabled={saving}
              onChange={(event) =>
                void save(
                  event.target.value === '' ? null : (event.target.value as ThemePreference),
                )
              }
            >
              {/* First, and an empty value rather than a fourth word. The server tells null
                  and an unrecognised word apart on purpose — reading a typo as "follow the
                  firm" would silently discard a decision somebody had made. */}
              <option value="">{t('fleet.drivers.followsFirm')}</option>

              {OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`theme.names.${option.toLowerCase()}`)}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>
    </Dialog>
  );
}
